import React, { useState, useEffect, useRef } from 'react';

interface Song {
  name: string;
  url: string;
}

const Winamp: React.FC = () => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [shuffle, setShuffle] = useState(false);
  const [loading, setLoading] = useState(true);

  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    fetchSongs();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (audioRef.current && !audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaElementSource(audioRef.current);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 64;
      source.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
    }
  }, [songs]);

  const fetchSongs = async () => {
    try {
      const response = await fetch('/api/files/music');
      const data = await response.json();
      setSongs(data.files || []);
    } catch (error) {
      console.error('Error fetching songs:', error);
      setSongs([]);
    } finally {
      setLoading(false);
    }
  };

  const drawVisualizer = () => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const barWidth = canvas.width / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const barHeight = (dataArray[i] / 255) * canvas.height;

      // Classic green bars
      ctx.fillStyle = `rgb(0, ${Math.floor(dataArray[i] + 100)}, 0)`;
      ctx.fillRect(x, canvas.height - barHeight, barWidth - 1, barHeight);

      x += barWidth;
    }

    animationRef.current = requestAnimationFrame(drawVisualizer);
  };

  const togglePlay = () => {
    if (!audioRef.current || songs.length === 0) return;

    if (isPlaying) {
      audioRef.current.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    } else {
      audioRef.current.play();
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
      drawVisualizer();
    }
    setIsPlaying(!isPlaying);
  };

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  const next = () => {
    if (songs.length === 0) return;
    let nextIndex;
    if (shuffle) {
      nextIndex = Math.floor(Math.random() * songs.length);
    } else {
      nextIndex = (currentIndex + 1) % songs.length;
    }
    setCurrentIndex(nextIndex);
    if (isPlaying && audioRef.current) {
      audioRef.current.play();
    }
  };

  const prev = () => {
    if (songs.length === 0) return;
    const prevIndex = (currentIndex - 1 + songs.length) % songs.length;
    setCurrentIndex(prevIndex);
    if (isPlaying && audioRef.current) {
      audioRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentSong = songs[currentIndex];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#000' }}>
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f0' }}>
          Loading...
        </div>
      ) : songs.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f0', textAlign: 'center', padding: '24px' }}>
          <div>
            <p>No music files uploaded yet.</p>
            <p style={{ fontSize: '12px', marginTop: '8px' }}>
              Upload MP3 files through the admin panel.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Visualizer */}
          <canvas
            ref={canvasRef}
            width={600}
            height={100}
            style={{ width: '100%', height: '100px', background: '#000' }}
          />

          {/* Display */}
          <div style={{
            background: '#000',
            color: '#0f0',
            padding: '8px',
            fontFamily: '"Courier New", monospace',
            fontSize: '14px',
            borderTop: '1px solid #0f0',
            borderBottom: '1px solid #0f0'
          }}>
            <div>NOW PLAYING: {currentSong?.name || 'No song'}</div>
            <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
          </div>

          {/* Timeline */}
          <div style={{ padding: '4px 8px', background: '#1a1a1a' }}>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              style={{ width: '100%' }}
            />
          </div>

          {/* Controls */}
          <div style={{
            background: '#1a1a1a',
            padding: '8px',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <button onClick={prev} style={{ minWidth: '50px' }}>⏮ Prev</button>
            <button onClick={togglePlay} style={{ minWidth: '50px' }}>
              {isPlaying ? '⏸ Pause' : '▶ Play'}
            </button>
            <button onClick={stop} style={{ minWidth: '50px' }}>⏹ Stop</button>
            <button onClick={next} style={{ minWidth: '50px' }}>Next ⏭</button>
            <button
              onClick={() => setShuffle(!shuffle)}
              style={{
                minWidth: '50px',
                background: shuffle ? '#0f0' : undefined,
                color: shuffle ? '#000' : undefined
              }}
            >
              🔀
            </button>
          </div>

          {/* Volume */}
          <div style={{
            background: '#1a1a1a',
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#0f0', fontSize: '11px' }}>VOL:</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              style={{ flex: 1 }}
            />
            <span style={{ color: '#0f0', fontSize: '11px', minWidth: '30px' }}>
              {Math.round(volume * 100)}%
            </span>
          </div>

          {/* Playlist */}
          <div style={{
            flex: 1,
            background: '#000',
            color: '#0f0',
            padding: '8px',
            overflow: 'auto',
            fontFamily: '"Courier New", monospace',
            fontSize: '11px',
            borderTop: '1px solid #0f0'
          }}>
            <div style={{ marginBottom: '4px', textDecoration: 'underline' }}>PLAYLIST:</div>
            {songs.map((song, index) => (
              <div
                key={index}
                style={{
                  padding: '2px 4px',
                  background: index === currentIndex ? '#0f0' : 'transparent',
                  color: index === currentIndex ? '#000' : '#0f0',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setCurrentIndex(index);
                  if (isPlaying && audioRef.current) {
                    audioRef.current.play();
                  }
                }}
              >
                {index + 1}. {song.name}
              </div>
            ))}
          </div>

          {/* Hidden audio element */}
          <audio
            ref={audioRef}
            src={currentSong?.url || `/assets/music/${currentSong?.name}`}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={next}
            crossOrigin="anonymous"
          />
        </>
      )}
    </div>
  );
};

export default Winamp;
